import { Schema } from "effect"
import { AgentKind, Evidence, ModelProvider, ModelsByAgent, Owned, Principal, Taint } from "./common"
import { Blocker, Hands, ImpossibleReason } from "./domain/task"
import {
  AgentId,
  AreaId,
  CommentId,
  CredentialId,
  Epoch,
  GoalId,
  InitiativeId,
  Millis,
  NonNegInt,
  PosInt,
  ProfileId,
  ProposalId,
  RunId,
  RunnerId,
  Sha256,
  ShortRef,
  Slug,
  TaskId,
  WakeId,
  WorkerRef,
} from "./ids"
import { ExclusiveKey, Placement, Unmet } from "./placement"
import { AccessRequest } from "./runner"

// #region run-kinds
/**
 * Every model call is a run, and so is every outward execution. A run is ONE attempt.
 * turn: one thinking turn of a persistent agent (sink: its Agent DO).
 * worker: one attempt at a task (sink: a Run DO).
 * validator: one review of a subject run, other model family by default (sink: a Run DO).
 * publish: executes exactly one human-approved outward tool call; no model (gateway, or a runner for stdio MCP).
 */
export const RunKind = Schema.Literals(["turn", "worker", "validator", "publish"])
export type RunKind = typeof RunKind.Type

export const Executor = Schema.TaggedUnion({
  runner: { runnerId: RunnerId, profileId: ProfileId, agent: AgentKind }, // includes one_job sandbox runners
  platform: { provider: ModelProvider, model: Schema.String, credentialId: Schema.NullOr(CredentialId) },
  gateway: {}, // publish runs for http integrations (Phase 2)
  hosted: { vendor: Schema.Literals(["anthropic_managed"]), externalId: Schema.String }, // later
})

/** Mechanical: why the process stopped. Set by the driver, the runner or the cloud — NEVER by the model. */
export const StopReason = Schema.Literals([
  "completed", // the model ended its turn on its own (Claude end_turn / Codex turn completed)
  "interrupted", // owner pressed stop, or a steer with priority "now" replaced the turn
  "max_turns", // truncated by the turn/step limit (the brief's "truncated")
  "context_exhausted", // ran out of context without a successful compaction
  "wall_clock", // exceeded the job's wall-clock limit
  "rate_limited", // provider quota on the profile
  "budget_exhausted", // reserved for v2 budgets; recorded so caps need no migration
  "auth_error", // CLI logged out / 401
  "policy_refused", // runner ceiling or permission profile refused the job
  "permission_denied", // a required tool permission was denied and the agent could not continue
  "sandbox_error", // worktree / sandbox / environment could not be prepared (after MAX_PRESTART_RELEASES)
  "cli_error", // driver crash, non-zero exit, protocol violation
  "runner_lost", // lease expired after the run started (cloud reaper)
  "superseded", // a newer epoch exists (a zombie reported)
  "cancelled", // cancelled before it started
  "mcp_unavailable", // Minga's own MCP server did not connect in the CLI, so the run could never report; it ended before the prompt
])
export type StopReason = typeof StopReason.Type

/** Semantic: what the agent claims, via the `report_outcome` MCP tool (last call wins). A claim, not a fact. */
export const RunOutcome = Schema.TaggedUnion({
  done: { summary: Schema.String, evidence: Schema.NonEmptyArray(Evidence) }, // → in_review, never straight to done
  progress: { summary: Schema.String, next: Schema.String }, // `next` seeds the next attempt's handoff
  blocked: { summary: Schema.String, blocker: Blocker },
  question: { askId: CommentId }, // the ask comment; its answer wakes the next attempt
  impossible: { reason: ImpossibleReason, explanation: Schema.String }, // first-class good exit
  unreported: {}, // never called report_outcome; never counts as done (normal for chat turns)
})
export type RunOutcome = typeof RunOutcome.Type

/** Validator runs only (`report_verdict`). The validator never sees the author's outcome summary. */
export const Verdict = Schema.TaggedUnion({
  pass: { evidence: Schema.NonEmptyArray(Evidence) },
  fail: {
    findings: Schema.NonEmptyArray(
      Schema.Struct({ severity: Schema.Literals(["blocker", "major", "minor"]), text: Schema.String }),
    ),
  },
  inconclusive: { why: Schema.String },
})

/** Recorded on RunEnded whenever the accepted outcome differs from the claim. */
export const Downgrade = Schema.Struct({
  from: Schema.Literals(["done", "progress", "blocked", "question", "impossible"]),
  rule: Schema.Literals(["not_completed", "evidence_missing", "evidence_unverifiable"]),
})

export const RunError = Schema.Struct({ code: Schema.String, message: Schema.String, fingerprint: Sha256 })
// #endregion run-kinds

// #region usage
/** Recorded on every run from day one; v2 budgets plug in without a migration. Unknown cost is null and treated as HIGH. */
export const Usage = Schema.Struct({
  provider: ModelProvider,
  model: Schema.String,
  profileId: Schema.NullOr(ProfileId),
  billing: Schema.Literals(["subscription_included", "metered", "unpriced"]), // never record subscription cost as $0
  inputTokens: NonNegInt,
  outputTokens: NonNegInt,
  cachedInputTokens: NonNegInt,
  reasoningTokens: NonNegInt,
  costMicroUsd: Schema.NullOr(NonNegInt),
  wallMs: NonNegInt,
  turns: NonNegInt,
  toolCalls: NonNegInt,
})

/** Live token counts of a run in progress (heartbeat `usageSoFar`, `RunActivity.usage`). Never a cost. */
export const ActivityUsage = Schema.Struct({
  inputTokens: NonNegInt,
  outputTokens: NonNegInt,
  cachedInputTokens: NonNegInt,
  reasoningTokens: NonNegInt,
})
export type ActivityUsage = typeof ActivityUsage.Type

/** Frozen when the run is requested, so cost attribution survives later re-parenting. */
export const Attribution = Schema.Struct({
  goalIds: Schema.Array(GoalId),
  initiativeId: Schema.NullOr(InitiativeId),
  areaId: Schema.NullOr(AreaId),
  taskId: Schema.NullOr(TaskId),
  agentId: Schema.NullOr(AgentId),
})
// #endregion usage

// #region session
/**
 * Native CLI session pointer: a cache, never the source of truth (the body's transcript is).
 * Claude: the body-backed SessionStore is keyed by sessionId (proven); projectKey is pinned to the agent or task id
 * via CLAUDE_CODE_PROJECT_DIR_NAME (only honoured when CLAUDE_CONFIG_DIR is set; must match ^[A-Za-z0-9_-]{1,64}$).
 * `resumableOn` becomes "anywhere" only once the mirror is confirmed complete (no mirror_error seen).
 * Codex: rollouts live under one CODEX_HOME, so native resume only on the same runner + profile.
 */
export const SessionRef = Schema.TaggedUnion({
  claude: {
    sessionId: Schema.String,
    projectKey: Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{1,64}$/)),
    lastExecutor: Schema.Struct({ runnerId: RunnerId, profileId: ProfileId }),
    resumableOn: Schema.Literals(["anywhere", "same_profile"]),
  },
  codex: {
    threadId: Schema.String,
    lastExecutor: Schema.Struct({ runnerId: RunnerId, profileId: ProfileId }),
    resumableOn: Schema.Literal("same_profile"),
  },
  platform: { throughMessageId: Schema.String }, // the body's own transcript cursor
})
export type SessionRef = typeof SessionRef.Type

/** What the next mind gets when it can't resume natively, or after other minds ran. Typed fields, never free text
 * from a possibly tainted run. Validator findings travel here into the next attempt. */
export const Handoff = Schema.Struct({
  fromRunIds: Schema.Array(RunId),
  mode: Schema.Literals(["delta", "full"]),
  goals: Schema.Array(Schema.String),
  decisions: Schema.Array(Schema.String),
  filesChanged: Schema.Array(Schema.String),
  openApprovals: Schema.Array(ProposalId),
  todos: Schema.Array(Schema.String),
  findings: Schema.Array(Schema.String),
})
// #endregion session

// #region run
/** Every runner→cloud write carries this pair. Every sink rejects epoch < its highest seen, or an ended run. */
export const LeaseRef = Schema.Struct({ runId: RunId, epoch: Epoch })

export const Lease = Schema.Struct({
  runId: RunId,
  epoch: Epoch,
  runnerId: RunnerId,
  runnerIncarnation: NonNegInt,
  profileId: ProfileId,
  grantedAt: Millis,
  expiresAt: Millis, // grantedAt + 60 s, pushed forward by Renew
  exclusive: Schema.Array(ExclusiveKey),
})

export const RunPhase = Schema.Literals(["preparing", "running", "awaiting_permission", "finishing"])

/** The Project DO's view. "offered" is hub-internal (≤ 10 s) and never reaches the log. */
export const RunState = Schema.TaggedUnion({
  requested: {},
  waiting: { unmet: Schema.Array(Unmet), since: Millis }, // "waiting for runner", with reasons
  leased: { epoch: Epoch, phase: RunPhase }, // phase "preparing" may be re-leased; from "running" on, loss ends the run
  ended: { stopReason: StopReason },
})

/** The newest milestone of a run, as `Run.lastMilestone` keeps it. */
export const RunMilestoneView = Schema.Struct({
  kind: Schema.String,
  text: Schema.String,
  ref: Schema.NullOr(Schema.String),
  at: Millis,
})

/** A worker or validator run as a temporary teammate (settings wave S7, decision 0025). */
export const RunWorker = Schema.Struct({
  ref: WorkerRef,
  /** The task's assignee: the persistent agent who supervises this run. */
  supervisorId: AgentId,
  /** The resolved worker preset's key (null only for runs without a preset). */
  preset: Schema.NullOr(Slug),
})
export type RunWorker = typeof RunWorker.Type

/** Where the run streams. The runner always dials it; no DO ever dials out. */
export const RunSink = Schema.TaggedUnion({ agent: { agentId: AgentId }, run_do: {}, none: {} })

export const Run = Schema.Struct({
  ...Owned, // ownerId = principal: whose runners and subscriptions may execute it (owner lock). Frozen at creation.
  id: RunId,
  ref: ShortRef, // R-2291
  kind: RunKind,
  agentId: Schema.NullOr(AgentId), // turn: the agent; worker/validator: the task's assignee
  taskId: Schema.NullOr(TaskId),
  subjectRunId: Schema.NullOr(RunId), // validator: the run under review
  publish: Schema.NullOr(Schema.Struct({ proposalId: ProposalId, revision: Schema.Int })),
  wakeBatchId: Schema.NullOr(WakeId), // turn: the coalesced wakes it answers
  attempt: PosInt, // per task for workers; per review round for validators; 1 for turns and publish
  /** worker (taskId, attempt) · validator (taskId, round, n) · turn (agentId, wakeBatchId) · publish (proposalId, revision). */
  idempotencyKey: Schema.String,
  attribution: Attribution,
  placement: Placement, // frozen effective placement: area defaults ⊕ task ⊕ mind ⊕ system constraints
  hands: Hands, // frozen
  context: Schema.NullOr(Schema.Struct({ hash: Sha256, manifestRef: Schema.String, taint: Taint })),
  sink: RunSink,
  state: RunState,
  epoch: Epoch, // highest epoch seen (0 until the first lease)
  prestartReleases: NonNegInt, // re-leases before "running"; capped by MAX_PRESTART_RELEASES
  executor: Schema.NullOr(Executor),
  claimed: Schema.NullOr(RunOutcome), // latest report_outcome claim
  verdict: Schema.NullOr(Verdict), // validators
  outcome: Schema.NullOr(RunOutcome), // the ACCEPTED outcome, set at RunEnded
  downgrade: Schema.NullOr(Downgrade),
  stopReason: Schema.NullOr(StopReason),
  error: Schema.NullOr(RunError),
  usage: Schema.Array(Usage),
  sessionIn: Schema.NullOr(SessionRef),
  sessionOut: Schema.NullOr(SessionRef),
  taint: Taint, // only ever rises during a run
  requestedAt: Millis,
  startedAt: Schema.NullOr(Millis),
  endedAt: Schema.NullOr(Millis),
  // Display facts (fix wave 2026-10-09 §4.2, decision 0022). Optional so old snapshots decode; never read by decide.
  /** RunLeased's time: "preparing since". */
  leasedAt: Schema.optionalKey(Millis),
  /** Start of the current phase: RunLeased, RunStarted, and a RunMilestone that changes the phase. */
  phaseSince: Schema.optionalKey(Millis),
  /** The newest RunMilestone (every kind); survives a reload. */
  lastMilestone: Schema.optionalKey(RunMilestoneView),
  /** The worktree branch from the frozen job (RunRequested.branch); null = scratch or no workspace. */
  branch: Schema.optionalKey(Schema.NullOr(Schema.String)),
  // Settings wave (§5.3). Set by decide at RequestRun; never read by decide afterwards.
  /** Workers and validators: the temporary teammate this run is. */
  worker: Schema.optionalKey(RunWorker),
  /** The narrowing request frozen with the run (turns: core K1; workers/validators: the preset). Only when it narrows. */
  access: Schema.optionalKey(AccessRequest),
  /** Workers/validators: model and effort per CLI family, for a run whose family the lease decides. */
  byAgent: Schema.optionalKey(ModelsByAgent),
  /** Who asked to stop it (decision 0032; RunCancelRequested's actor): display and the stop's `TaskPaused.by`. */
  cancelRequestedBy: Schema.optionalKey(Schema.NullOr(Principal)),
})
export type Run = typeof Run.Type
// #endregion run
